/**
 * One derived index per card document — the module's read path.
 *
 * THE PROBLEM
 * -----------
 * The card stores its data as three parallel arrays (`fieldOrder`,
 * `enabledFieldIds`, `placements`), which is the right *storage* shape: it is
 * flat, it serialises cleanly, and it is the shape the published contract
 * describes. It is the wrong *lookup* shape. Every read was a scan:
 *
 *   selectPlacement       card.placements.find(…)        O(p)
 *   selectIsFieldEnabled  card.enabledFieldIds.includes  O(e)
 *   selectRegionBlocks    card.placements.filter(…)      O(p)
 *
 * and the configuration pane asks those questions once per field, for 29
 * fields, on every render — so the pane alone was O(29 × p). The preview then
 * ran three more full scans for its three regions, plus a re-grouping of the
 * Info rows, on every keystroke into a Display label.
 *
 * THE INDEX
 * ---------
 * Built once per document into hash structures, so every one of those questions
 * becomes O(1) and the whole index costs a single O(p + f) pass.
 *
 * WHY A WeakMap AND NOT useMemo
 * -----------------------------
 * The cache key is the card object's own identity. `normalizeCard` returns a new
 * object for every mutation and the same object when nothing changed, so
 * identity already *is* the "has this card changed?" answer — there is no
 * dependency array to get wrong, and a selector called from three components
 * builds the index once rather than three times. `WeakMap` means a superseded
 * document is collected with its index; nothing accumulates.
 *
 * The second, quieter win: the arrays handed back are **stable across renders**.
 * `card.placements.filter(…)` returned a new array every call, so a `React.memo`
 * on any region component compared new-array against new-array and re-rendered
 * regardless. Now an unchanged card yields the identical array, and memoisation
 * downstream actually holds.
 *
 * The returned collections are shared, so **callers must treat them as
 * read-only**. Nothing in the module mutates a selector result today; this is
 * the note that keeps that true.
 */

import { DEFAULT_PHOTO_ANCHOR, PHOTO_ANCHORS } from '../constants';
import {
  BlockPlacement,
  FieldId,
  InfoRow,
  PhotoAnchor,
  ProductCard,
  RegionId,
} from '../types';
import { groupInfoRows } from './normalize';

export interface CardIndex {
  /** Placement per field — the `find` that ran once per row, now a hash hit. */
  placementByField: ReadonlyMap<FieldId, BlockPlacement>;
  /** Enabled fields — the `includes` that ran once per row. */
  enabled: ReadonlySet<FieldId>;
  /** A field's position in the rail order, for anything that compares sequence. */
  positionByField: ReadonlyMap<FieldId, number>;
  /** Placements per region, in render order. */
  blocksByRegion: ReadonlyMap<RegionId, readonly BlockPlacement[]>;
  /** Photo overlays bucketed by anchor; every anchor has an entry, possibly empty. */
  photoByAnchor: Readonly<Record<PhotoAnchor, readonly BlockPlacement[]>>;
  /** Info rows with side-by-side pairing already resolved. */
  infoRows: readonly InfoRow[];
}

const EMPTY_BLOCKS: readonly BlockPlacement[] = Object.freeze([]);

const buildIndex = (card: ProductCard): CardIndex => {
  const placementByField = new Map<FieldId, BlockPlacement>();
  const blocksByRegion = new Map<RegionId, BlockPlacement[]>([
    ['photo', []],
    ['info', []],
    ['floating', []],
  ]);
  const photoByAnchor = {} as Record<PhotoAnchor, BlockPlacement[]>;
  PHOTO_ANCHORS.forEach((anchor) => {
    photoByAnchor[anchor.id] = [];
  });

  // One pass over the placements fills all three views of them. `placements` is
  // already sorted by field order (normalizeCard guarantees it), so pushing in
  // iteration order preserves render order without a second sort.
  card.placements.forEach((placement) => {
    if (!placementByField.has(placement.fieldId)) {
      placementByField.set(placement.fieldId, placement);
    }
    blocksByRegion.get(placement.region)?.push(placement);

    if (placement.region === 'photo') {
      // `sticky` belongs to the footer; a photo placement carrying it is a
      // document that has been edited by hand, and it lands on the default slot
      // rather than being dropped. Same rule selectPhotoBlocksByAnchor applied.
      const anchor =
        placement.anchor && placement.anchor !== 'sticky'
          ? placement.anchor
          : DEFAULT_PHOTO_ANCHOR;
      photoByAnchor[anchor].push(placement);
    }
  });

  const positionByField = new Map<FieldId, number>();
  card.fieldOrder.forEach((fieldId, position) => {
    positionByField.set(fieldId, position);
  });

  return {
    placementByField,
    enabled: new Set(card.enabledFieldIds),
    positionByField,
    blocksByRegion,
    photoByAnchor,
    infoRows: groupInfoRows(blocksByRegion.get('info') ?? []),
  };
};

/**
 * Keyed on the document's identity. A card that has not been re-created since
 * the last call gets its existing index; the entry disappears with the document.
 */
const CACHE = new WeakMap<ProductCard, CardIndex>();

export const indexCard = (card: ProductCard): CardIndex => {
  const cached = CACHE.get(card);
  if (cached) {
    return cached;
  }
  const index = buildIndex(card);
  CACHE.set(card, index);
  return index;
};

/** Placements in one region, in render order. Empty array for an empty region. */
export const regionBlocks = (card: ProductCard, region: RegionId): readonly BlockPlacement[] =>
  indexCard(card).blocksByRegion.get(region) ?? EMPTY_BLOCKS;
